import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { pollWindowVariable } from '@mooncake/mooncake-gateway-core/utils';
import { withContent } from '@bubbletea/core';
import Title from '../../UI/Title';
import Tooltip from '../../UI/Tooltip';
import { IContent } from '../../../types';
import { IStoreState } from '../../../types/store';
import {
  ContentWrapper,
  HeaderSection,
  PlansSection,
  PlatformSection,
  CancelAnytimeContainer,
  CancelAnytimeText,
} from './styles';
import PlanCard from './PlanCard';
import { IIbexPricingDetail } from '../../../types/subscription';
import sendEvent from '../../../utils/sendEvent';
import findCurrentSubscription from '../../../utils/findCurrentSubscription';

interface IPlansProps {
  context: IContent;
  inactive?: boolean;
}

type TIbexPricingDetails = {
  subscriptions: IIbexPricingDetail[];
};

const Plans = ({ context, inactive = false }: IPlansProps) => {
  const [impressionEventRegistered, setImpressionEventRegistered] = useState(false);
  const contentSelector = useSelector((state: IStoreState) => ({
    locale: state.coreApp.locale,
    analyticsLoaded: state.app.analyticsLoaded,
  }));
  const { analyticsLoaded, locale } = contentSelector;

  useEffect(() => {
    const impressionEventDetails = {
      location: 'available plans',
      category: 'scroll',
      locationPosition: 4,
      action: 'visible',
    };
    if (analyticsLoaded && impressionEventRegistered) {
      setImpressionEventRegistered(false);
      sendEvent(impressionEventDetails, 'impressionevent');
    }
  }, [impressionEventRegistered, analyticsLoaded]);

  const { googlesheet } = context;
  const { plans, promoOffers } = googlesheet;
  const [ibexPricingDetails, setIbexPricingDetails] = useState<TIbexPricingDetails>({ subscriptions: [] });

  useEffect(() => {
    const fetchIbexPricingDetails = async () => {
      const pricingDetails = await pollWindowVariable<TIbexPricingDetails>('window.ibexPricingDetails');
      setIbexPricingDetails(pricingDetails);
    };
    fetchIbexPricingDetails();
    return () => {
      setIbexPricingDetails({ subscriptions: [] });
    };
  }, []);

  const cancelAnytimeTrackingDetails = {
    location: 'available plans',
    locationDetail: 'cancel anytime',
    category: 'display',
    locationPosition: 'tooltip',
    action: 'visible',
  };

  return (
    <ContentWrapper
      id={'plans'}
      whileInView={'sendevent'}
      onAnimationStart={() => setImpressionEventRegistered(true)}
      viewport={{ once: true }}
    >
      <div className="section-wrapper">
        <HeaderSection>
          <Title uppercase>{plans.title}</Title>
          <PlatformSection>
            <p>{plans.platform}</p>
            {plans.platforms.map((platform) => (
              <span key={platform}>{platform}</span>
            ))}
          </PlatformSection>
          <CancelAnytimeContainer>
            <CancelAnytimeText>{plans.cancelAnytime}</CancelAnytimeText>
            <Tooltip tracking={cancelAnytimeTrackingDetails}>{plans.cancelAnytimeDetails}</Tooltip>
          </CancelAnytimeContainer>
        </HeaderSection>
        <PlansSection>
          {ibexPricingDetails &&
            plans.plansItems.map((plan, index) => {
              const currentSubscription = findCurrentSubscription(
                ibexPricingDetails,
                context.googlesheet.products,
                plan.productId
              );

              const oneMonthSub = findCurrentSubscription(ibexPricingDetails, context.googlesheet.products, 'oneMonth');

              return (
                <PlanCard
                  locale={locale}
                  ibexPricingDetail={currentSubscription.subscription}
                  oneMonthSubscription={oneMonthSub.subscription}
                  key={plan.id}
                  plan={plan}
                  index={index}
                />
              );
            })}
        </PlansSection>
        {inactive ? <p>{promoOffers.caption}</p> : null}
      </div>
    </ContentWrapper>
  );
};

export default withContent(Plans);
